// src/components/BarcodeScanner.js

import React, { useEffect, useRef, useContext, useState } from 'react';
import Quagga from 'quagga';
import { Modal, Button } from 'react-bootstrap';
import { LanguageContext } from '../contexts/LanguageContext';
import useAlertDialog from '../hooks/useAlertDialog';
import AlertDialog from './AlertDialog';
import './BarcodeScanner.css'; // Import the CSS for overlay

const BarcodeScanner = ({ show, handleClose, onDetected }) => {
  const { translations } = useContext(LanguageContext);
  const { dialogState: alertState, showAlert, hideAlert } = useAlertDialog();
  const scannerRef = useRef(null);
  const modalRef = useRef(null); // Reference to the modal for focus management
  const [isMobile, setIsMobile] = useState(false); // State to track device type

  // Function to check device type based on window width
  const checkDeviceType = () => {
    const width = window.innerWidth;
    if (width <= 768) { // You can adjust this breakpoint as needed
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    // Initial check
    checkDeviceType();

    // Add event listener for window resize
    window.addEventListener('resize', checkDeviceType);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', checkDeviceType);
    };
  }, []);

  useEffect(() => {
    if (show) {
      Quagga.init(
        {
          inputStream: {
            type: 'LiveStream',
            constraints: {
              video: {
                facingMode: 'environment',
                focusMode: 'continuous', // Attempt continuous focus
                // You can add more constraints here if needed
              },
            },
            target: scannerRef.current,
            area: { // Define scanning area if needed
              top: "0%",    // top offset
              right: "0%",  // right offset
              bottom: "0%", // bottom offset
              left: "0%"    // left offset
            },
          },
          locator: {
            patchSize: 'medium',
            halfSample: true,
          },
          numOfWorkers: navigator.hardwareConcurrency || 4,
          decoder: {
            readers: ['ean_reader', 'code_128_reader', 'upc_reader'], // Add or remove barcode formats as needed
          },
          locate: true,
        },
        function (err) {
          if (err) {
            console.log(err);
            showAlert({
              title: translations.error || 'Error',
              message: translations.scannerInitError || 'Failed to initialize scanner.',
              variant: 'danger',
            });
            return;
          }
          Quagga.start();
        }
      );

      Quagga.onDetected(handleDetected);

      // Focus the scanner container when the modal is shown
      setTimeout(() => {
        if (scannerRef.current) {
          scannerRef.current.focus();
        }
      }, 500); // Delay to ensure the modal is fully rendered

      return () => {
        Quagga.offDetected(handleDetected);
        Quagga.stop();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleDetected = (result) => {
    const code = result.codeResult.code;
    console.log('Barcode detected and processed : [' + code + ']', result);
    onDetected(code);
    handleClose();
  };

  return (
    <>
      {/* Alert Dialog */}
      <AlertDialog
        show={alertState.show}
        onHide={hideAlert}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        icon={alertState.icon}
        autoClose={alertState.autoClose}
        autoCloseDelay={alertState.autoCloseDelay}
        showCloseButton={alertState.showCloseButton}
      />
      
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        autoFocus
        enforceFocus
        backdrop="static" // Prevent closing by clicking backdrop
        keyboard={false} // Prevent closing with Esc key
        ref={modalRef}
      >
      <Modal.Header closeButton>
        <Modal.Title>{translations.scanBarcode}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="barcode-scanner-container">
        <div
          ref={scannerRef}
          className="scanner-video"
          tabIndex="-1" // Make the div focusable
        />
        {/* Overlay rendered only on mobile or tablet devices */}
        {isMobile && (
          <div className="barcode-overlay">
            <div className="barcode-border" />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {translations.cancel}
        </Button>
      </Modal.Footer>
      </Modal>
    </>
  );
};

export default BarcodeScanner;
